import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import logger from 'Utils/Logger';
import StartGame from './main';
import {
  EventBus,
  NotifyPhaser,
  ResizeWindow,
  ClearAllPhaserQueues,
  ClearAllReactQueues,
} from './EventBus';

export interface IRefPhaserGame {
  game: Phaser.Game | null;
  scene: Phaser.Scene | null;
  /** True when the game was reused from a previous mount (singleton). */
  wasReused: boolean;
}

interface IProps {
  currentActiveScene?: (scene_instance: Phaser.Scene) => void;
  width: number;
  height: number;
  scaling?: number;
  crisp?: boolean;
  playButtonOverlap?: number;
}

/**
 * Module-level singleton for the Phaser game instance and its canvas.
 *
 * On iOS WKWebView, creating a new WebGL context after destroying one crashes
 * the process. By keeping a single game alive for the lifetime of the app we
 * avoid ever needing to create a second WebGL context.
 *
 * On unmount we stop all running scenes, clear EventBus listeners and queues,
 * sleep the game loop, and detach the canvas from the DOM. On remount we
 * reattach the canvas, wake the game loop, and let the container start a
 * fresh scene.
 */
let singletonGame: Phaser.Game | null = null;
let singletonCanvas: HTMLCanvasElement | null = null;

/**
 * Resume the Phaser game's WebAudio context if it was suspended.
 * Browsers suspend the AudioContext when the tab is hidden or inactive.
 * Eagerly resuming it avoids a pop/distortion on the first sound after wake.
 */
function resumePhaserAudioContext(game: Phaser.Game): void {
  try {
    const ctx = (game.sound as Phaser.Sound.WebAudioSoundManager)?.context;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch {
    // Not a WebAudio sound manager or context unavailable — safe to ignore
  }
}

/**
 * Fully destroy a Phaser game and release its WebGL resources.
 * Only used for context-loss recovery — not for normal unmount.
 */
function destroyGame(game: Phaser.Game): void {
  const canvas = game.canvas;
  const renderer = game.renderer;

  game.destroy(true);

  if (canvas) {
    canvas.width = 0;
    canvas.height = 0;
  }

  if (renderer && 'gl' in renderer && renderer.gl) {
    const loseCtx = renderer.gl.getExtension('WEBGL_lose_context');
    if (loseCtx) {
      loseCtx.loseContext();
    }
  }
}

function destroySingletonGame(): void {
  if (!singletonGame) return;
  destroyGame(singletonGame);
  singletonGame = null;
  singletonCanvas = null;
}

// ---------------------------------------------------------------------------
// Singleton helpers
// ---------------------------------------------------------------------------

function getOrCreateSingletonGame(
  parentId: string,
  width: number,
  height: number,
  scaling?: number,
  crisp?: boolean,
): { game: Phaser.Game; wasReused: boolean } {
  if (singletonGame) {
    const container = document.getElementById(parentId);
    if (container && singletonCanvas && !container.contains(singletonCanvas)) {
      container.appendChild(singletonCanvas);
    }

    // Check WebGL context state before wake
    const gl = (singletonGame.renderer as Phaser.Renderer.WebGL.WebGLRenderer)?.gl;
    const ctxLost = typeof gl?.isContextLost === 'function' ? gl.isContextLost() : 'n/a';
    singletonGame.loop?.wake?.();

    // Update the Scale Manager to match the new consumer's config.
    // The game may have been created with different scale settings (e.g.
    // tutorial uses Scale.FIT at 1920x1080; lessons use Scale.NONE with
    // crisp/DPR sizing). Without this the canvas CSS dimensions remain
    // stale and the content may be invisible or mis-sized.
    const dpr = window.devicePixelRatio || 1;
    const scaleMode = scaling ?? Phaser.Scale.FIT;
    const gameWidth = crisp ? width * dpr : width;
    const gameHeight = crisp ? height * dpr : height;

    // Update the scale manager's parent reference to the new container.
    // On reuse the canvas is re-parented to a new game-container div,
    // but the scale manager's internal `parent` still points at the old
    // (unmounted) element.  In FIT mode Phaser measures that stale parent
    // and computes 0×0, making the canvas invisible.
    if (container) {
      const scaleMgr = singletonGame.scale as unknown as Record<string, unknown>;
      scaleMgr.parent = container;
    }

    // scaleMode is readonly in the TS defs but writable at runtime
    (singletonGame.scale as { scaleMode: number }).scaleMode = scaleMode;
    singletonGame.scale.resize(gameWidth, gameHeight);

    // Set canvas CSS explicitly on reuse.
    if (singletonCanvas) {
      if (scaleMode === Phaser.Scale.NONE) {
        // NONE + crisp: canvas resolution is DPR-scaled, CSS is viewport size
        singletonCanvas.style.width = `${width}px`;
        singletonCanvas.style.height = `${height}px`;
      } else {
        // FIT / other modes: set to 100% so it fills the parent immediately,
        // then schedule a refresh so Phaser re-measures the (now correct)
        // parent and computes proper fitted dimensions.
        singletonCanvas.style.width = '100%';
        singletonCanvas.style.height = '100%';
        requestAnimationFrame(() => {
          singletonGame?.scale?.refresh?.();
        });
      }
    }
    resumePhaserAudioContext(singletonGame);

    return { game: singletonGame, wasReused: true };
  }

  singletonGame = StartGame(parentId, width, height, scaling, crisp);
  singletonCanvas = singletonGame.canvas;
  return { game: singletonGame, wasReused: false };
}

/**
 * Stop all running/active scenes. This triggers each scene's `shutdown` event,
 * which in turn calls the scene's `destroy()` / `unload()` method to clean up
 * game objects, event listeners, and internal state.
 */
function stopAllScenes(game: Phaser.Game): void {
  game.scene.scenes.forEach((scene: Phaser.Scene) => {
    if (scene.sys?.isActive() || scene.sys?.isPaused()) {
      game.scene.stop(scene.scene.key);
    }
  });
}

function sleepSingletonGame(): void {
  if (singletonGame) {
    singletonGame.loop?.sleep?.();
  }
}

function detachCanvas(): void {
  if (singletonCanvas && singletonCanvas.parentElement) {
    singletonCanvas.parentElement.removeChild(singletonCanvas);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PhaserGame = forwardRef<IRefPhaserGame, IProps>(function PhaserGame(
  { currentActiveScene, width, height, scaling, crisp, playButtonOverlap },
  ref,
) {
  const game = useRef<Phaser.Game | null>(null!);
  const [_phaserReady, setPhaserReady] = useState(false);

  // --- Context-loss recovery ---
  const restartGame = () => {
    try {
      destroySingletonGame();
    } catch (err) {
      logger.warn('failed to destroy Phaser game before restart', err);
    }
    const result = getOrCreateSingletonGame('game-container', width, height);
    game.current = result.game;
    if (typeof ref === 'function') {
      ref({ game: game.current, scene: null, wasReused: false });
    } else if (ref) {
      ref.current = { game: game.current, scene: null, wasReused: false };
    }
    logger.info('Phaser game restarted after resume/context loss');
  };

  const wakeGame = () => {
    const currentGame = game.current;
    if (!currentGame) {
      restartGame();
      return;
    }
    const gl = (currentGame.renderer as Phaser.Renderer.WebGL.WebGLRenderer)?.gl;
    const contextLost = typeof gl?.isContextLost === 'function' ? gl.isContextLost() : false;
    if (contextLost) {
      restartGame();
      return;
    }
    currentGame.loop?.wake?.();
    currentGame.renderer?.resize?.(width, height);
    resumePhaserAudioContext(currentGame);
    NotifyPhaser(ResizeWindow(Math.max(width, 960), height, playButtonOverlap));
  };

  const sleepGame = () => {
    game.current?.loop?.sleep?.();
  };

  // --- Mount (once) ---
  // Create or reuse the singleton game on first mount only. Heavy cleanup
  // lives in the unmount-only effect below so that re-renders caused by
  // width/height changes don't tear down EventBus listeners.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const result = getOrCreateSingletonGame('game-container', width, height, scaling, crisp);
    game.current = result.game;

    const activeScene = result.wasReused
      ? (game.current.scene.scenes.find((s: Phaser.Scene) => s.sys?.isActive()) ??
        game.current.scene.scenes[0] ??
        null)
      : null;

    if (typeof ref === 'function') {
      ref({ game: game.current, scene: activeScene, wasReused: result.wasReused });
    } else if (ref) {
      ref.current = { game: game.current, scene: activeScene, wasReused: result.wasReused };
    }
  }, []); // mount-only — size updates handled by Game's resize handlers

  // --- Unmount cleanup (once) ---
  // Separated from the mount effect so that re-renders (e.g. from
  // width/height prop changes) don't nuke EventBus listeners that
  // sibling components have registered.
  useEffect(() => {
    return () => {
      if (game.current) {
        stopAllScenes(game.current);
      }
      EventBus.removeAllListeners();
      ClearAllReactQueues();
      ClearAllPhaserQueues();
      sleepSingletonGame();
      detachCanvas();
    };
  }, []);

  // --- Visibility / app-state ---
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        sleepGame();
      } else {
        wakeGame();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    let appStateSub: { remove?: () => void } | undefined;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          wakeGame();
        } else {
          sleepGame();
        }
      })
        .then((handle) => {
          appStateSub = handle;
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      appStateSub?.remove?.();
    };
  }, [height, width]);

  // --- Scene-ready bridge ---
  useEffect(() => {
    EventBus.on('current-scene-ready', (scene_instance: Phaser.Scene) => {
      if (currentActiveScene && typeof currentActiveScene === 'function') {
        currentActiveScene(scene_instance);
      }
      if (typeof ref === 'function') {
        ref({ game: game.current, scene: scene_instance, wasReused: false });
      } else if (ref) {
        ref.current = { game: game.current, scene: scene_instance, wasReused: false };
      }
      setPhaserReady(true);
    });
    return () => {
      EventBus.removeListener('current-scene-ready');
    };
  }, [currentActiveScene, ref]);

  return (
    <div
      id="game-container"
      style={{
        pointerEvents: 'none',
      }}
    ></div>
  );
});
